import { SizeChartData } from '@components/pages/static/SizeChart'
import trackError from '@helpers/trackError'
import {
  getSizeChartCategory,
  getSizeChartProductType,
  SizeChartCollection
} from '@helpers/sizeChart'
import axios from 'axios'

const sizeChartByProductTypeQuery = `query SizeChartQuery($product_type: String!) {
  sizeChart(product_type: $product_type) {
    items {
      build
      cut
      leg
      measurements {
        key
        value
      }
      product_measurement_type
      product_type
    }
  }
}`

export const getSizeChart = async (
  productType: string
): Promise<{ sizeChart: SizeChartData }> => {
  const { data } = await axios({
    method: 'post',
    url: `https://${process.env.CONTENT_API_DOMAIN}/graphql`,
    headers: {
      Accept: 'application/json',
      'x-api-key': process.env.CONTENT_API_API_KEY!
    },
    data: {
      query: sizeChartByProductTypeQuery,
      variables: { product_type: productType }
    }
  })

  return data.data
}

export const hasSizeChartData = async (productType: string) => {
  try {
    const { sizeChart } = await getSizeChart(productType)

    return Boolean(
      sizeChart?.items?.some(({ measurements }) => measurements?.length)
    )
  } catch (e) {
    const error = e as Error
    trackError(new Error('Size chart data fetch failed'), {
      error,
      productType,
      section: 'Size Chart'
    })

    return false
  }
}

type SizeChartMeasurementTypesResponse = Record<
  string,
  {
    items: {
      product_measurement_type: string
      measurements: { key: string }[]
    }[]
  } | null
>

// Batches every product type into a single request using aliases
const getSizeChartMeasurementTypesQuery = (productTypes: string[]) => {
  const variables = productTypes
    .map((_, index) => `$type${index}: String!`)
    .join(', ')
  const fields = productTypes
    .map(
      (_, index) =>
        `type${index}: sizeChart(product_type: $type${index}) { items { product_measurement_type measurements { key } } }`
    )
    .join('\n')

  return `query SizeChartMeasurementTypesQuery(${variables}) {\n${fields}\n}`
}

const getSizeChartMeasurementTypes = async (productTypes: string[]) => {
  const { data } = await axios({
    method: 'post',
    url: `https://${process.env.CONTENT_API_DOMAIN}/graphql`,
    headers: {
      Accept: 'application/json',
      'x-api-key': process.env.CONTENT_API_API_KEY!
    },
    data: {
      query: getSizeChartMeasurementTypesQuery(productTypes),
      variables: productTypes.reduce<Record<string, string>>(
        (result, productType, index) => ({
          ...result,
          [`type${index}`]: productType
        }),
        {}
      )
    }
  })

  if (!data?.data) {
    throw new Error('Size chart measurement types are missing')
  }

  // Product types without a size chart come back as errors alongside the data, so only data is read
  const sizeCharts: SizeChartMeasurementTypesResponse = data.data

  return productTypes.reduce<Record<string, string[]>>(
    (result, productType, index) => {
      const items = (sizeCharts[`type${index}`]?.items || []).filter(
        ({ measurements }) => measurements?.length
      )

      if (items.length) {
        result[productType] = items.map(
          ({ product_measurement_type }) => product_measurement_type
        )
      }

      return result
    },
    {}
  )
}

export const getSizeChartCollection = async (
  productTypes: string[]
): Promise<SizeChartCollection> => {
  const labelsBySizeChartProductType = productTypes.reduce<
    Record<string, string>
  >((result, productType) => {
    const sizeChartProductType = getSizeChartProductType(productType)

    if (!result[sizeChartProductType]) {
      result[sizeChartProductType] = productType
    }

    return result
  }, {})
  const sizeChartProductTypes = Object.keys(labelsBySizeChartProductType)

  const measurementTypesByProductType = sizeChartProductTypes.length
    ? await getSizeChartMeasurementTypes(sizeChartProductTypes)
    : {}

  return Object.entries(measurementTypesByProductType)
    .sort(([a], [b]) =>
      labelsBySizeChartProductType[a].localeCompare(
        labelsBySizeChartProductType[b]
      )
    )
    .reduce<SizeChartCollection>(
      (collection, [sizeChartProductType, measurementTypes]) => {
        let category = getSizeChartCategory(
          sizeChartProductType,
          measurementTypes
        )

        if (!category) {
          trackError(new Error('Size chart category not recognised'), {
            productType: sizeChartProductType,
            measurementTypes: measurementTypes.join(', '),
            section: 'Size Chart'
          })
          category = 'accessories'
        }

        collection[category] = {
          ...collection[category],
          [sizeChartProductType]:
            labelsBySizeChartProductType[sizeChartProductType]
        }

        return collection
      },
      {}
    )
}
